import React, { ReactElement, useEffect, useMemo, useRef } from 'react';
import { InjectedWizardStepProps } from '../../../components/Steps/WizardStep';
import { DataCollectionValues, useDataCollection } from '../../../../hooks';
import { Form } from 'formik';
import FormControl from '../../../components/Form/FormControl';
import { regionsOptions, buildingPhaseOptions } from '../../../../data/enumerations';
import StepHeader from '../../../components/Layout/StepHeader';
import InfoWarningModal from '../../../components/Modal/InfoWarningModal';
import callBasicInfoApi from '../../../../api/wizard/callBasicInfoApi';
interface FormError {
    area?: string;
    areaMin?: string;
    areaMax?: string;
    region?: string;
    buildingPhase?: string;
}

const stepValidation = (values: DataCollectionValues) => {
    const errors: FormError = {};

    if (!values.area) {
        errors.area = 'Povinné pole';
    }

    if (values.area && Number(values.area) < 10) {
        errors.areaMin = 'Minimální možná plocha je 10 m2';
    }

    if (values.area && Number(values.area) > 350) {
        errors.areaMax = 'Plocha nad 350 m2 je řešena individuálně';
    }

    if (!values.region) {
        errors.region = 'Povinné pole';
    }

    if (!values.buildingPhase) {
        errors.buildingPhase = 'Povinné pole';
    }

    return errors;
};

const StepBuildingBasicInfo = ({ completeStep }: InjectedWizardStepProps): ReactElement<'div'> | null => {
    const { handleChange, handleBlur, values } = useDataCollection();

    const errors = useMemo(() => stepValidation(values), [values]);
    useEffect(() => {
        completeStep && completeStep(!Object.keys(errors).length);
    }, [errors]);

    const refValues = useRef(values);
    refValues.current = values;

    const sendBasicInfoData = () => {
        callBasicInfoApi({
            area: refValues.current.area,
            region: refValues.current.region,
            buildingPhase: refValues.current.buildingPhase,
        });
    };

    useEffect(
        () => () => {
            sendBasicInfoData();
        },
        [],
    );

    return (
        <>
            {errors.areaMax && <InfoWarningModal />}
            <Form>
                <input type="hidden" tabIndex={1} />
                <div className="otz-main__header">
                    <h1 className="otz-main__title">
                        <strong>Základní údaje <span className="block md-inline">o Vašem domě</span></strong>
                    </h1>
                    <p className="otz-main__subtitle c-gray">Díky této informaci Vám ukážeme přesnější výsledky pro váš dům.</p>
                </div>
                <div className="otz-house-info">
                    <div className="otz-house-info__item">
                        <div className="otz-house-info__inner">
                            <div className="otz-house-info__icon">
                                <img src="/img/icon/modal-house-icon-1.svg" alt="otz-house-icon" draggable="false" />
                            </div>
                            <div className="otz-house-info__title">Podlahová plocha</div>
                            <div className="otz-house-info__select">
                                <div className="form-container form-container--text">
                                    <FormControl
                                        name="area"
                                        id="areaTotal"
                                        type="number"
                                        tag="input"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.area}
                                        error={errors.area || errors.areaMin || errors.areaMax}
                                        min={10}
                                        max={350}
                                    />
                                    <div className="wiz-form-text">
                                        m<sup>2</sup>
                                    </div>
                                </div>
                            </div>
                            <div
                                className={`otz-house-info__error ${
                                    values.area && Number(values.area) < 10 ? 'is-active' : ''
                                }`}
                            >
                                Minimální možná plocha je 10 m<sup>2</sup>
                            </div>
                        </div>
                    </div>
                    <div className="otz-house-info__item">
                        <div className="otz-house-info__inner">
                        <div className="otz-house-info__icon">
                                <img src="/img/icon/modal-house-icon-2.svg" alt="otz-house-icon" draggable="false" />
                            </div>
                            <div className="otz-house-info__title">Kraj</div>
                            <div className="otz-house-info__select">
                                <FormControl
                                    name="region"
                                    id="region"
                                    placeholder="Zvolte kraj"
                                    tag="select"
                                    options={regionsOptions}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.region}
                                    error={errors.region}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="otz-house-info__item">
                        <div className="otz-house-info__inner">
                        <div className="otz-house-info__icon">
                                <img src="/img/icon/modal-house-icon-3.svg" alt="otz-house-icon" draggable="false" />
                            </div>
                            <div className="otz-house-info__title">Fáze stavby</div>
                            <div className="otz-house-info__select">
                                <FormControl
                                    name="buildingPhase"
                                    id="buildingPhase"
                                    placeholder="Fáze stavby"
                                    tag="select"
                                    options={buildingPhaseOptions}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.buildingPhase}
                                    error={errors.buildingPhase}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div id="otz-modal-warning-app"></div>
            </Form>
        </>
    );
};

export default StepBuildingBasicInfo;
